// useUnit.js

import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"

import {
  getUnitList,
  getUnitById,
  createUnit,
  updateUnit,
  deleteUnit,
  getTrashedUnits,
  restoreUnit,
  forceDeleteUnit,
  getUnitBySlug,
} from "api/unitApi"

import {
  useIsFetching,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

export const UNIT_QUERY_KEY = "units"

// -------------------- FETCH PAGINATED --------------------
export const useUnits = (params = {}, enabled = true) => {
  return usePaginatedResource(
    UNIT_QUERY_KEY,
    getUnitList,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useUnit = (id, enabled = true) => {
  return useResourceItem(
    UNIT_QUERY_KEY,
    getUnitById,
    id,
    enabled
  )
}

// -------------------- FETCH BY SLUG --------------------
export const useUnitBySlug = (slug, enabled = true) => {
  return useResourceItem(
    [UNIT_QUERY_KEY, "slug"],
    getUnitBySlug,
    slug,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateUnit = () => {
  return useCreateResource(
    createUnit,
    UNIT_QUERY_KEY
  )
}

// -------------------- EDIT --------------------
export const useEditUnit = () => {
  return useEditResource(
    updateUnit,
    UNIT_QUERY_KEY
  )
}

// -------------------- DELETE (Soft Delete) --------------------
export const useDeleteUnit = () => {
  return useDeleteResource(
    deleteUnit,
    UNIT_QUERY_KEY
  )
}

// -------------------- FORCE DELETE (Permanent Delete) --------------------
export const useForceDeleteUnit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: forceDeleteUnit,
    onSuccess: (_, id) => {
      // Remove from trashed cache
      queryClient.setQueriesData(
        [UNIT_QUERY_KEY, "trashed"],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = updated.data.data.filter(
            item => item.id !== id
          )
          updated.data.total = Math.max(
            0,
            (updated.data.total || 1) - 1
          )
          return updated
        }
      )

      // Remove individual cache
      queryClient.removeQueries([UNIT_QUERY_KEY, id])
    },
  })
}

// -------------------- ADDITIONAL --------------------

// ✅ Get all (no pagination)
export const useAllUnits = (params = {}) => {
  return usePaginatedResource(
    UNIT_QUERY_KEY,
    getUnitList,
    { ...params, all: true },
    true
  )
}

// ✅ Get trashed (soft-deleted)
export const useTrashedUnits = (params = {}) => {
  return usePaginatedResource(
    [UNIT_QUERY_KEY, "trashed"],
    getTrashedUnits,
    params,
    true
  )
}

// ✅ Restore trashed item
export const useRestoreUnit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: restoreUnit,
    onSuccess: (res, id) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Remove from trashed cache
      queryClient.setQueriesData(
        [UNIT_QUERY_KEY, "trashed"],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = updated.data.data.filter(
            item => item.id !== id
          )
          updated.data.total = Math.max(
            0,
            (updated.data.total || 1) - 1
          )
          return updated
        }
      )

      // Add to main list cache
      queryClient.setQueriesData(
        [UNIT_QUERY_KEY],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = [restoredItem, ...updated.data.data]
          updated.data.total = (updated.data.total || 0) + 1
          return updated
        }
      )

      // Update individual cache
      queryClient.setQueryData(
        [UNIT_QUERY_KEY, id],
        restoredItem
      )
    },
  })
}

// -------------------- SEARCH (PROGRAMMATIC) --------------------
export const useUnitSearchAPI = ({
  all = true,
  limit = 5,
} = {}) => {
  const queryClient = useQueryClient()

  const unitSearchAPI = async searchTerm => {
    const queryKey = [
      "unit-searching",
      { search: searchTerm, all, limit },
    ]

    const cached = queryClient.getQueryData(queryKey)
    if (cached) return cached

    return queryClient.fetchQuery({
      queryKey,
      queryFn: () =>
        getUnitList({
          search: searchTerm,
          all: true,
        }),
    })
  }

  const loading =
    useIsFetching({
      queryKey: ["unit-searching"],
    }) > 0

  return { unitSearchAPI, loading }
}
